import { useState } from "react";

import { isAPIError } from "@/core/services";
import { REQUIRED_MESSAGE, useToast } from "@/ds";
import type { ConfirmDialogProps } from "@/ds";
import { criarEscopoMacro } from "@/modules/financeiro-obras";
import type { AlvoSimulacao } from "@/modules/financeiro-obras";
import { useChaveIdempotencia, useConfirmarDescarte } from "@/shared/hooks";
import { MOTIVO_SEM_PERMISSAO_TIPO } from "../planejamento-controle.constants";
import { SEM_TIPO_ELEGIVEL } from "../novo-tipo-obra-dialog/novo-tipo-obra-dialog.constants";
import type { EscolhaNovoTipo } from "../novo-tipo-obra-dialog";
import { useIdentificacaoEquipamento } from "../../../../hooks/use-identificacao-equipamento";
import {
  useEscoposPadraoDoTipoDados,
  useOrigensEscopoMacroDados,
} from "../../../../hooks/use-planejamento-dados";
import { FEL_ZERO } from "../planejamento-controle.cancelamento";
import { GMUD_TIPO_BLOQUEADO } from "../planejamento-controle.constants";
import type { CorpoProjecaoFinanceira } from "../projecao-formulario";
import { useEtapaProjecao } from "./etapa-projecao/use-etapa-projecao";
import type { ResumoEscopo } from "./etapa-projecao";
import { montarIdentificacaoEquipamento } from "./identificacao-equipamento/identificacao-equipamento.modelo";
import {
  ADICIONAR_CENTRO_LABEL,
  ADICIONAR_CENTRO_VALOR,
  ADICIONAR_TIPO_LABEL,
  ADICIONAR_TIPO_VALOR,
  CRIADO_AO_CONCLUIR_TAG,
  ENTRA_AO_SALVAR_AJUDA,
  CAMPO_TROCA,
  CANCELAR_LABEL,
  EM_APROVACAO_TAG,
  ERRO_ENVIO_PADRAO,
  ESCOPO_JA_INCLUIDO,
  ESCOPOS_PADRAO_VAZIO,
  ETAPA_DADOS,
  ETAPA_PROJECAO,
  FALHA_ORIGENS,
  PROXIMA_LABEL,
  SALVAR_SEM_PROJECAO_LABEL,
  SEM_TIPO_DISPONIVEL,
  TEXTOS_POR_FEL,
  TROCA_ACEITAR_LABEL,
  TROCA_HEADER,
  TROCA_MANTER_LABEL,
  TROCA_MESSAGE,
  VOLTAR_LABEL,
  SEM_CENTRO_CUSTO_ELEGIVEL,
} from "./adicionar-escopo-macro-dialog.constants";
import {
  PENDENTE_TIPO_VALOR,
  centrosComPendente,
  opcoesDeCentroNovo,
  opcoesDoCentro,
} from "./adicionar-escopo-macro-dialog.pacote";
import {
  RASCUNHO_VAZIO,
  comunsPreenchidos,
  formaDoFormulario,
  limparAPartirDe,
  montarRequest,
  rascunhoPreenchido,
  temAlternancia,
  validarRascunho,
} from "./adicionar-escopo-macro-dialog.rascunho";
import type {
  Alternativa,
  CampoForm,
  ErrosForm,
  Rascunho,
} from "./adicionar-escopo-macro-dialog.rascunho";
import type {
  AdicionarEscopoMacroDialogProps,
  EtapaDoEscopo,
  EtapaEscopo,
  OpcaoEscopo,
} from "./types-adicionar-escopo-macro-dialog";

type Rascunhos = Record<Alternativa, Rascunho>;
const RASCUNHOS_VAZIOS: Rascunhos = { PADRAO: RASCUNHO_VAZIO, PERSONALIZADO: RASCUNHO_VAZIO };

interface TrocaPendente {
  campo: keyof typeof CAMPO_TROCA;
  aplicar: () => void;
}

function errosDoServidor(details: unknown): ErrosForm {
  const campos = new Set<string>(Object.keys(RASCUNHO_VAZIO));
  const alias: Record<string, CampoForm> = { escopo: "nome" };
  const achados: [string, string][] = Array.isArray(details)
    ? details.map((item: { field?: string; message?: string }) => [
        item.field ?? "",
        item.message ?? "",
      ])
    : Object.entries((details ?? {}) as Record<string, unknown>).map(([campo, mensagem]) => [
        campo,
        String(mensagem),
      ]);
  const erros: ErrosForm = {};
  for (const [campo, mensagem] of achados) {
    const destino = alias[campo] ?? (campos.has(campo) ? (campo as CampoForm) : null);
    if (destino && mensagem) erros[destino] = mensagem;
  }
  return erros;
}

export function useAdicionarEscopoMacroDialog({
  obraId,
  fel,
  obraInicio,
  obraFim,
  obra,
  cotacaoDaObra,
  centrosCusto: centrosDaObra,
  opcoesNovoTipo,
  onClose,
  onConcluido,
  onCotacaoAlterada,
}: AdicionarEscopoMacroDialogProps) {
  const toast = useToast();
  const { comChave } = useChaveIdempotencia();
  const [pendente, setPendente] = useState<EscolhaNovoTipo | null>(null);
  const [subfluxo, setSubfluxo] = useState<"centro" | "tipo" | null>(null);
  const centrosCusto = centrosComPendente(centrosDaObra, pendente);
  const principal = centrosDaObra.find((centro) => centro.principal) ?? null;
  const centroInicial = principal?.id ?? "";

  const [etapa, setEtapa] = useState<EtapaEscopo>(1);
  const [centroCustoId, setCentroCustoId] = useState(centroInicial);
  const [obraTipoObraId, setObraTipoObraId] = useState("");
  const [alternativa, setAlternativa] = useState<Alternativa>("PADRAO");
  const [rascunhos, setRascunhos] = useState<Rascunhos>(RASCUNHOS_VAZIOS);
  const [tentou, setTentou] = useState(false);
  const [errosServidor, setErrosServidor] = useState<ErrosForm>({});
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [troca, setTroca] = useState<TrocaPendente | null>(null);
  const [projecaoAberta, setProjecaoAberta] = useState(false);

  const centro = centrosCusto.find((item) => item.id === centroCustoId) ?? null;
  const tiposSelecionaveis = (centro?.tipos ?? []).filter((tipo) => !tipo.cancelamentoPendente);
  const tipo = tiposSelecionaveis.find((item) => item.obraTipoObraId === obraTipoObraId) ?? null;
  const semTipoDisponivel = centro !== null && tiposSelecionaveis.length === 0;

  const fonte = tipo?.fonteEscopo ?? null;
  const alternativaEfetiva: Alternativa =
    fonte !== null && temAlternancia(fonte) ? alternativa : "PERSONALIZADO";
  const forma = fonte ? formaDoFormulario(fonte, alternativaEfetiva) : null;
  const rascunho = rascunhos[alternativaEfetiva];
  const periodo = { inicio: obraInicio, fim: obraFim };

  const origens = useOrigensEscopoMacroDados(forma !== null);
  const escoposPadrao = useEscoposPadraoDoTipoDados(
    forma === "CATALOGO" ? (tipo?.tipoObraId ?? null) : null,
  );

  const errosDaTela: ErrosForm = forma ? validarRascunho(rascunho, forma, periodo) : {};
  const erros: ErrosForm = { ...(tentou ? errosDaTela : {}), ...errosServidor };
  for (const campo of ["inicio", "fim"] as const) {
    if (rascunho[campo] && errosDaTela[campo]) erros[campo] = errosDaTela[campo];
  }

  function alterar(parcial: Partial<Rascunho>) {
    setRascunhos((atual) => ({
      ...atual,
      [alternativaEfetiva]: { ...atual[alternativaEfetiva], ...parcial },
    }));
    setErrosServidor((atual) => {
      const restante = { ...atual };
      for (const campo of Object.keys(parcial)) delete restante[campo as CampoForm];
      return restante;
    });
    setErroEnvio(null);
  }

  function substituirPadrao(proximo: Rascunho) {
    setRascunhos((atual) => ({ ...atual, PADRAO: proximo }));
    setErrosServidor({});
    setErroEnvio(null);
  }

  function pedirTroca(campo: TrocaPendente["campo"], temDadoAbaixo: boolean, aplicar: () => void) {
    if (temDadoAbaixo) setTroca({ campo, aplicar });
    else aplicar();
  }

  function pedirTrocaNoPadrao(campo: "grupo" | "equipamento", anterior: string, novo: string) {
    if (novo === anterior) return;
    const chave = campo === "grupo" ? "grupo" : "equipamentoSfiId";
    const abaixo =
      campo === "grupo"
        ? rascunho.equipamentoSfiId !== "" ||
          rascunho.tipoManutencao !== "" ||
          comunsPreenchidos(rascunho)
        : rascunho.tipoManutencao !== "" || comunsPreenchidos(rascunho);
    pedirTroca(campo, anterior !== "" && abaixo, () =>
      substituirPadrao({ ...limparAPartirDe(rascunhos.PADRAO, chave), [chave]: novo }),
    );
  }

  const dadosEquipamento = useIdentificacaoEquipamento({
    obraId,
    ativo: forma === "EQUIPAMENTO",
    grupo: rascunho.grupo,
    equipamentoSfiId: rascunho.equipamentoSfiId,
    tipoManutencao: rascunho.tipoManutencao,
  });
  const equipamento = montarIdentificacaoEquipamento({
    dados: dadosEquipamento,
    rascunho,
    erros,
    escolherGrupo: (grupo) => pedirTrocaNoPadrao("grupo", rascunho.grupo, grupo),
    escolherEquipamento: (id) => pedirTrocaNoPadrao("equipamento", rascunho.equipamentoSfiId, id),
    escolherTipoManutencao: (valor) => alterar({ tipoManutencao: valor }),
  });

  const preenchido =
    rascunhoPreenchido(rascunhos.PADRAO) || rascunhoPreenchido(rascunhos.PERSONALIZADO);

  function limparFormulario() {
    etapaProjecao.descartar();
    setRascunhos(RASCUNHOS_VAZIOS);
    setAlternativa("PADRAO");
    setTentou(false);
    setErrosServidor({});
    setErroEnvio(null);
  }

  const idsDaObra = centrosDaObra.map((item) => item.id);
  const centrosNovos = opcoesNovoTipo ? opcoesDeCentroNovo(opcoesNovoTipo, idsDaObra) : null;
  const tiposNovos =
    opcoesNovoTipo && centro && !centro.pendente ? opcoesDoCentro(opcoesNovoTipo, centro.id) : null;
  const carregandoOpcoes = opcoesNovoTipo?.status === "loading";
  const semCentroNovo =
    opcoesNovoTipo?.status === "success" && (centrosNovos?.data?.centrosCusto.length ?? 0) === 0;
  const semTipoNovo =
    opcoesNovoTipo?.status === "success" && (tiposNovos?.data?.centrosCusto.length ?? 0) === 0;
  const podeAdicionarCentro = opcoesNovoTipo !== null && !carregandoOpcoes && !semCentroNovo;
  const podeAdicionarTipo = tiposNovos !== null && !carregandoOpcoes && !semTipoNovo;

  function escolherCentroCusto(id: string) {
    if (id === ADICIONAR_CENTRO_VALOR) {
      if (podeAdicionarCentro) setSubfluxo("centro");
      return;
    }
    if (id === centroCustoId) return;
    pedirTroca("centroCusto", preenchido || etapaProjecao.isDirty, () => {
      setCentroCustoId(id);
      setObraTipoObraId("");
      limparFormulario();
    });
  }

  function escolherTipoObra(id: string) {
    if (id === ADICIONAR_TIPO_VALOR) {
      if (podeAdicionarTipo) setSubfluxo("tipo");
      return;
    }
    if (id === obraTipoObraId) return;
    pedirTroca("tipoObra", preenchido || etapaProjecao.isDirty, () => {
      setObraTipoObraId(id);
      limparFormulario();
    });
  }

  function escolherNoSubfluxo(escolha: EscolhaNovoTipo) {
    setSubfluxo(null);
    setPendente(escolha);
    pedirTroca("tipoObra", preenchido || etapaProjecao.isDirty, () => {
      setCentroCustoId(escolha.centroCusto.id);
      setObraTipoObraId(PENDENTE_TIPO_VALOR);
      limparFormulario();
    });
  }

  const opcoesCentro: OpcaoEscopo[] = [
    ...centrosCusto.map((item) => ({
      label: item.rotulo,
      value: item.id,
      tag: item.pendente ? CRIADO_AO_CONCLUIR_TAG : null,
    })),
    {
      label: ADICIONAR_CENTRO_LABEL,
      value: ADICIONAR_CENTRO_VALOR,
      disabled: !podeAdicionarCentro,
      tagTitulo: semCentroNovo
        ? SEM_CENTRO_CUSTO_ELEGIVEL
        : opcoesNovoTipo === null
          ? MOTIVO_SEM_PERMISSAO_TIPO
          : null,
    },
  ];

  const opcoesTipo: OpcaoEscopo[] = [
    ...(centro?.tipos ?? []).map((item) => ({
      label: item.rotulo,
      value: item.obraTipoObraId,
      disabled: item.cancelamentoPendente,
      tag: item.cancelamentoPendente
        ? EM_APROVACAO_TAG
        : item.pendente
          ? CRIADO_AO_CONCLUIR_TAG
          : null,
      tagTitulo: item.cancelamentoPendente ? GMUD_TIPO_BLOQUEADO() : null,
    })),
    ...(tiposNovos
      ? [
          {
            label: ADICIONAR_TIPO_LABEL,
            value: ADICIONAR_TIPO_VALOR,
            disabled: !podeAdicionarTipo,
            tagTitulo: semTipoNovo ? SEM_TIPO_ELEGIVEL : null,
          },
        ]
      : []),
  ];

  const catalogo = escoposPadrao.data.filter(
    (item, indice, lista) => lista.findIndex((outro) => outro.escopo === item.escopo) === indice,
  );
  const incluidos = new Set(tipo?.escoposIncluidos ?? []);
  const opcoesCatalogo: OpcaoEscopo[] = catalogo.map((item) => ({
    label: item.escopo,
    value: item.id,
    disabled: incluidos.has(item.escopo),
    tag: incluidos.has(item.escopo) ? ESCOPO_JA_INCLUIDO : null,
  }));
  const catalogoVazio =
    forma === "CATALOGO" && escoposPadrao.status === "success" && catalogo.length === 0;

  const efetivacaoImediata = fel === FEL_ZERO;
  const textos = efetivacaoImediata ? TEXTOS_POR_FEL.efetivacaoImediata : TEXTOS_POR_FEL.aprovacao;
  const aviso = efetivacaoImediata ? null : TEXTOS_POR_FEL.aprovacao.aviso(fel);

  const bloqueioDaForma = catalogoVazio || (forma === "EQUIPAMENTO" && equipamento.bloqueado);
  const podeAvancar = centro !== null && tipo !== null && !bloqueioDaForma;
  const erroCentro = tentou && centro === null ? REQUIRED_MESSAGE : null;
  const erroTipo = tentou && tipo === null ? REQUIRED_MESSAGE : null;

  function valido(): boolean {
    setTentou(true);
    return forma !== null && Object.keys(errosDaTela).length === 0;
  }

  const naProjecao = etapa === 2;
  const pacote = tipo?.pendente && pendente ? pendente.pacote : null;
  const destino = pacote ?? (tipo ? { obraTipoObraId: tipo.obraTipoObraId } : null);
  const alvo: AlvoSimulacao | null = pacote
    ? { centroCustoId: pacote.centroCustoId, tipoObraId: pacote.tipoObraId }
    : tipo
      ? { obraTipoObraId: tipo.obraTipoObraId }
      : null;

  const nomeDoEscopo =
    forma === "TEXTO"
      ? rascunho.nome.trim()
      : forma === "CATALOGO"
        ? (catalogo.find((item) => item.id === rascunho.escopoMacroId)?.escopo ?? "")
        : (dadosEquipamento.composto?.nome ?? "");
  const resumo: ResumoEscopo = {
    tipoObra: tipo?.rotulo ?? "",
    centroCusto: centro?.rotulo ?? "",
    escopo: nomeDoEscopo,
    inicio: rascunho.inicio?.toLocaleDateString("pt-BR") ?? "",
    fim: rascunho.fim?.toLocaleDateString("pt-BR") ?? "",
    origem: origens.data.find((item) => item.valor === rascunho.origemId)?.rotulo ?? "",
  };

  async function enviar(projecao?: CorpoProjecaoFinanceira) {
    if (enviando) return;
    if (!valido() || !podeAvancar || !destino || !forma) return;
    const base = montarRequest(destino, forma, alternativaEfetiva, rascunho);
    if (!base) return;
    const body = projecao ? { ...base, projecao } : base;
    setEnviando(true);
    setErroEnvio(null);
    try {
      const resposta = await comChave(body, (idempotencyKey) =>
        criarEscopoMacro(obraId, body, { idempotencyKey }),
      );
      const sucesso = projecao
        ? resposta.message || textos.sucessoComProjecao
        : textos.sucessoSemProjecao(nomeDoEscopo);
      if (textos.sucessoTom === "success") toast.success(sucesso);
      else toast.info(sucesso);
      onConcluido();
    } catch (erro) {
      const doCampo = isAPIError(erro) && erro.status === 400 ? errosDoServidor(erro.details) : {};
      setErrosServidor(doCampo);
      if (projecao || Object.keys(doCampo).length === 0)
        setErroEnvio((isAPIError(erro) && erro.message) || ERRO_ENVIO_PADRAO);
    } finally {
      setEnviando(false);
    }
  }

  const etapaProjecao = useEtapaProjecao({
    obraId,
    ativa: projecaoAberta,
    alvo,
    obra,
    cotacaoDaObra,
    resumo,
    enviar,
    enviando,
  });

  function avancar() {
    if (valido() && podeAvancar) {
      setProjecaoAberta(true);
      setEtapa(2);
    }
  }

  function fechar() {
    if (etapaProjecao.cotacaoAlterada) onCotacaoAlterada?.();
    onClose();
  }
  const descarte = useConfirmarDescarte({
    onDescartar: fechar,
    testId: "adicionar-escopo-descarte",
  });

  function cancelar() {
    descarte.pedir(
      centroCustoId !== centroInicial ||
        obraTipoObraId !== "" ||
        preenchido ||
        pendente !== null ||
        etapaProjecao.isDirty,
    );
  }

  const etapas: EtapaDoEscopo[] = [
    {
      numero: 1,
      titulo: ETAPA_DADOS,
      situacao: etapa === 1 ? "active" : "done",
      ariaLabel: `Etapa 1: ${ETAPA_DADOS}`,
      testId: "adicionar-escopo-etapa-1",
      onSelect: etapa === 2 ? () => setEtapa(1) : undefined,
    },
    {
      numero: 2,
      titulo: ETAPA_PROJECAO,
      situacao: etapa === 2 ? "active" : "pending",
      ariaLabel: `Etapa 2: ${ETAPA_PROJECAO}`,
      testId: "adicionar-escopo-etapa-2",
      onSelect: undefined,
    },
  ];

  const confirmacaoTroca: ConfirmDialogProps | null = troca
    ? {
        visible: true,
        variant: "destructive",
        header: TROCA_HEADER(CAMPO_TROCA[troca.campo]),
        message: TROCA_MESSAGE,
        acceptLabel: TROCA_ACEITAR_LABEL,
        rejectLabel: TROCA_MANTER_LABEL,
        accept: () => {
          troca.aplicar();
          setTroca(null);
        },
        reject: () => setTroca(null),
        onHide: () => setTroca(null),
        testId: "adicionar-escopo-troca",
      }
    : null;

  return {
    etapa,
    etapas,
    projecao: etapaProjecao.view,
    cotacaoDialog: etapaProjecao.cotacaoDialog,
    aviso,
    centroCusto: {
      value: centroCustoId || null,
      opcoes: opcoesCentro,
      erro: erroCentro,
      ajuda: centro?.pendente ? ENTRA_AO_SALVAR_AJUDA : null,
    },
    tipoObra: {
      value: obraTipoObraId || null,
      opcoes: opcoesTipo,
      desabilitado: centro === null,
      semOpcao: semTipoDisponivel ? SEM_TIPO_DISPONIVEL : null,
      erro: erroTipo,
      ajuda: tipo?.pendente ? ENTRA_AO_SALVAR_AJUDA : null,
    },
    formulario:
      forma === null
        ? null
        : {
            forma,
            alternancia: fonte !== null && temAlternancia(fonte) ? alternativa : null,
            nome: { value: rascunho.nome, erro: erros.nome ?? null },
            catalogo: {
              value: rascunho.escopoMacroId || null,
              opcoes: opcoesCatalogo,
              erro: erros.escopoMacroId ?? null,
              vazio: catalogoVazio ? ESCOPOS_PADRAO_VAZIO : null,
              falha: escoposPadrao.status === "error" ? escoposPadrao.errorMessage : null,
            },
            equipamento: equipamento.viewModel,
            inicio: { value: rascunho.inicio, erro: erros.inicio ?? null },
            fim: { value: rascunho.fim, erro: erros.fim ?? null },
            origem: {
              value: rascunho.origemId || null,
              opcoes: origens.data.map((item) => ({ label: item.rotulo, value: item.valor })),
              erro: erros.origemId ?? (origens.status === "error" ? FALHA_ORIGENS : null),
            },
            descricao: { value: rascunho.descricao, erro: erros.descricao ?? null },
          },
    novoTipo:
      subfluxo && opcoesNovoTipo
        ? {
            fel,
            opcoes:
              subfluxo === "centro"
                ? opcoesDeCentroNovo(opcoesNovoTipo, idsDaObra)
                : opcoesDoCentro(opcoesNovoTipo, centroCustoId),
            onClose: () => setSubfluxo(null),
            onEscolher: escolherNoSubfluxo,
          }
        : null,
    erroEnvio,
    enviando,
    rodape: {
      cancelarLabel: naProjecao ? VOLTAR_LABEL : CANCELAR_LABEL,
      cancelarTestId: naProjecao ? "adicionar-escopo-voltar" : "adicionar-escopo-cancel",
      onCancelar: naProjecao ? () => setEtapa(1) : cancelar,
      confirmarLabel: naProjecao ? textos.concluirLabel : PROXIMA_LABEL,
      confirmarDesabilitado: enviando,
      confirmarCarregando: naProjecao && (enviando || etapaProjecao.view.estado === "loading"),
      onConfirmar: naProjecao ? etapaProjecao.concluir : avancar,
      salvarSemProjecao: naProjecao
        ? null
        : {
            label: SALVAR_SEM_PROJECAO_LABEL,
            desabilitado: enviando,
            carregando: enviando,
            onClick: () => void enviar(),
          },
    },
    escolherCentroCusto,
    escolherTipoObra,
    escolherAlternativa: (proxima: Alternativa) => {
      setAlternativa(proxima);
      setErrosServidor({});
      setErroEnvio(null);
    },
    digitarNome: (nome: string) => alterar({ nome }),
    escolherEscopoPadrao: (id: string) => alterar({ escopoMacroId: id }),
    alterarInicio: (inicio: Date | null) => alterar({ inicio }),
    alterarFim: (fim: Date | null) => alterar({ fim }),
    escolherOrigem: (origemId: string) => alterar({ origemId }),
    digitarDescricao: (descricao: string) => alterar({ descricao }),
    cancelar,
    descarte: descarte.confirmacao,
    confirmacaoTroca,
  };
}
